import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import { SwaggerFile, SwaggerFileDetails } from '../../types/swaggerFile';
import { errorMessage } from './swaggerFileModel';

/**
 * Swagger file list plus CRUD. Mutations patch local state from the API response
 * instead of refetching the whole list.
 */
export function useSwaggerFiles() {
  const [files, setFiles] = useState<SwaggerFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setFiles(await apiService.getAllSwaggerFiles());
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err, 'Failed to load swagger files'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const upload = useCallback(async (file: File, { description, tags }: SwaggerFileDetails): Promise<SwaggerFile> => {
    const created: SwaggerFile = await apiService.uploadSwaggerFile(file, description, tags);
    setFiles((prev) => [created, ...prev.filter((f) => f.id !== created.id)]);
    return created;
  }, []);

  const update = useCallback(async (id: string, details: SwaggerFileDetails) => {
    const updated: SwaggerFile = await apiService.updateSwaggerFile(id, details);
    setFiles((prev) => prev.map((f) => (f.id === id ? updated : f)));
  }, []);

  const remove = useCallback(async (id: string) => {
    await apiService.deleteSwaggerFile(id);
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  return { files, loading, loadError, reload, upload, update, remove };
}
